import type { LayerInspectorSelection } from "./layerInspectorTools";

export type LayerBinding = {
  layerUid?: number;
  layerName?: string;
  assetId?: string;
};

export type AssetBinding = {
  imageName: string;
};

export type LayerIdentity = LayerBinding & {
  layerPath?: string;
  instancePath?: string;
};

type CollectLayerBindingsOptions = {
  maxDepth?: number;
};

type ConnectorLayerSelectionOptions = CollectLayerBindingsOptions & {
  fallbackAssetName?: string;
};

type ConnectorLayerInfo = {
  uid?: number;
  nm?: string;
  ty?: number;
  refId?: string;
};

type ConnectorAssetInfo = {
  id?: string;
  nm?: string;
  p?: string;
  layers?: ConnectorLayerInfo[];
};

type ConnectorJsonFile = {
  layers?: ConnectorLayerInfo[];
  assets?: ConnectorAssetInfo[];
};

export function normalizeLayerKeyPart(value?: string): string | undefined {
  if (!value) return undefined;

  const normalized = value.trim().toLowerCase();
  return normalized || undefined;
}

export function buildAssetBindingKey(
  binding: AssetBinding
): string | undefined {
  const normalized = normalizeLayerKeyPart(binding.imageName);
  return normalized ? `imageName:${normalized}` : undefined;
}

export function buildLayerBindingKeys(binding: LayerBinding): string[] {
  const keys: string[] = [];

  if (typeof binding.layerUid === "number") {
    keys.push(`uid:${binding.layerUid}`);
  }

  const normalizedName = normalizeLayerKeyPart(binding.layerName);
  if (!normalizedName) {
    return keys;
  }

  const normalizedAsset = normalizeLayerKeyPart(binding.assetId);
  if (normalizedAsset) {
    keys.push(`asset:${normalizedAsset}::${normalizedName}`);
  }

  keys.push(`name::${normalizedName}`);
  return keys;
}

export function buildLayerIdentityKeys(layer: LayerIdentity): string[] {
  const keys: string[] = [];

  if (layer.instancePath) {
    keys.push(`instance:${layer.instancePath}`);
  }

  keys.push(
    ...buildLayerBindingKeys({
      layerUid: layer.layerUid,
      layerName: layer.layerName,
      assetId: layer.assetId,
    })
  );

  if (layer.layerPath) {
    keys.push(`path:${layer.layerPath}`);
  }

  return keys;
}

export function buildLayerInspectorSelectionKey(
  selection: LayerInspectorSelection | null | undefined,
  fallbackAssetName?: string
): string {
  if (!selection) {
    return "";
  }

  if (selection.instancePath) {
    return `instance:${selection.instancePath}`;
  }

  if (typeof selection.layerUid === "number") {
    return `uid:${selection.layerUid}`;
  }

  if (selection.path) {
    return `path:${selection.path}`;
  }

  const [primaryKey] = buildLayerBindingKeys({
    layerUid: selection.layerUid,
    layerName: selection.layerName,
    assetId: selection.assetName ?? fallbackAssetName,
  });

  return primaryKey ?? "";
}

export function buildLayerInspectorKeySet(
  ...groups: Array<LayerInspectorSelection[] | undefined>
): Set<string> {
  const keys = new Set<string>();

  groups.forEach((group) => {
    group?.forEach((selection) => {
      const key = buildLayerInspectorSelectionKey(selection);
      if (key) {
        keys.add(key);
      }
    });
  });

  return keys;
}

export function getNextLayerInspectorSelections({
  currentSelections,
  selection,
  additive = false,
}: {
  currentSelections: LayerInspectorSelection[];
  selection: LayerInspectorSelection;
  additive?: boolean;
}): LayerInspectorSelection[] {
  if (!additive) {
    return [selection];
  }

  const selectionKey = buildLayerInspectorSelectionKey(selection);
  const currentKeys = new Set(
    currentSelections.map((entry) => buildLayerInspectorSelectionKey(entry))
  );

  if (currentKeys.has(selectionKey)) {
    return currentSelections.filter(
      (entry) => buildLayerInspectorSelectionKey(entry) !== selectionKey
    );
  }

  return [...currentSelections, selection];
}

export function layerMatchesInspectorKeySet(
  layer: LayerIdentity,
  keySet: Set<string>
): boolean {
  if (keySet.size === 0) {
    return false;
  }

  return buildLayerIdentityKeys(layer).some((key) => keySet.has(key));
}

export function collectLayerBindings(
  value: unknown,
  options: CollectLayerBindingsOptions = {}
): LayerBinding[] {
  const bindings: LayerBinding[] = [];
  const visited = new WeakSet<object>();
  const maxDepth = options.maxDepth ?? Number.POSITIVE_INFINITY;

  function visit(
    node: unknown,
    inheritedAssetId?: string,
    depth: number = 0
  ): void {
    if (depth > maxDepth) return;
    if (!node || typeof node !== "object") return;
    if (visited.has(node as object)) return;

    visited.add(node as object);

    if (Array.isArray(node)) {
      node.forEach((entry) => visit(entry, inheritedAssetId, depth + 1));
      return;
    }

    const obj = node as Record<string, unknown>;
    const assetId =
      typeof obj.assetId === "string" && obj.assetId
        ? obj.assetId
        : inheritedAssetId;
    const layerUid =
      typeof obj.layerUid === "number" ? obj.layerUid : undefined;
    const layerName =
      typeof obj.layerName === "string" ? obj.layerName : undefined;

    if (layerUid !== undefined || layerName) {
      bindings.push({
        layerUid,
        layerName,
        assetId,
      });
    }

    Object.values(obj).forEach((entry) => {
      visit(entry, assetId, depth + 1);
    });
  }

  visit(value);
  return bindings;
}

export function collectAssetBindings(value: unknown): AssetBinding[] {
  if (!value || typeof value !== "object") {
    return [];
  }

  const imageName = (value as { imageName?: unknown }).imageName;
  if (typeof imageName !== "string" || imageName.length === 0) {
    return [];
  }

  return [{ imageName }];
}

export function buildConnectorLayerInspectorSelections(
  value: unknown,
  jsonFile?: ConnectorJsonFile,
  options: ConnectorLayerSelectionOptions = {}
): LayerInspectorSelection[] {
  const fallbackAssetName = options.fallbackAssetName ?? "main";
  const normalizedFallbackAssetName = normalizeLayerKeyPart(fallbackAssetName);
  const selections: LayerInspectorSelection[] = [];
  const seenKeys = new Set<string>();

  const addSelection = (selection: LayerInspectorSelection) => {
    const key = buildLayerInspectorSelectionKey(selection, fallbackAssetName);
    if (!key || seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    selections.push(selection);
  };

  collectLayerBindings(value, { maxDepth: options.maxDepth ?? 10 }).forEach(
    (binding) => {
      const normalizedAssetId = normalizeLayerKeyPart(binding.assetId);
      addSelection({
        path: "",
        assetName:
          binding.assetId && normalizedAssetId !== normalizedFallbackAssetName
            ? binding.assetId
            : undefined,
        layerName: binding.layerName,
        layerUid: binding.layerUid,
      });
    }
  );

  const targetImageNames = new Set(
    collectAssetBindings(value)
      .map((binding) => normalizeLayerKeyPart(binding.imageName))
      .filter((imageName): imageName is string => Boolean(imageName))
  );

  if (!targetImageNames.size || !jsonFile) {
    return selections;
  }

  const assetById = new Map<string, ConnectorAssetInfo>();
  jsonFile.assets?.forEach((asset) => {
    if (asset.id) {
      assetById.set(asset.id, asset);
    }
  });

  const addMediaLayerSelections = (
    layers: ConnectorLayerInfo[] | undefined,
    pathPrefix: string,
    assetName?: string
  ) => {
    layers?.forEach((layer, index) => {
      if (!layer.refId) {
        return;
      }

      const referencedAsset = assetById.get(layer.refId);
      const referencedImageName = normalizeLayerKeyPart(referencedAsset?.p);
      if (!referencedImageName || !targetImageNames.has(referencedImageName)) {
        return;
      }

      const path = `${pathPrefix}[${index}]`;
      addSelection({
        path,
        instancePath: path,
        assetName,
        layerName: layer.nm,
        layerUid: layer.uid,
        layerType: layer.ty,
        refId: layer.refId,
      });
    });
  };

  addMediaLayerSelections(jsonFile.layers, "layers");
  jsonFile.assets?.forEach((asset, assetIndex) => {
    addMediaLayerSelections(
      asset.layers,
      `assets[${assetIndex}].layers`,
      asset.nm ?? asset.id
    );
  });

  return selections;
}
